import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { Calendar, AlertTriangle, CheckCircle, ArrowRight, Clock, RefreshCw } from 'lucide-react';

const CancellationPage = () => {
  const cancellationTiers = [
    {
      period: "Plus de 60 jours",
      color: "green",
      refund: "100%",
      fee: "Aucun frais",
      description: "Annulation gratuite avec remboursement intégral de votre acompte et du solde"
    },
    {
      period: "30 à 60 jours",
      color: "amber", 
      refund: "50%",
      fee: "50% de retenue",
      description: "Remboursement partiel, idéal pour les changements de dernière minute"
    },
    {
      period: "Moins de 30 jours",
      color: "red",
      refund: "0%",
      fee: "100% de retenue", 
      description: "Aucun remboursement possible, mais transfert sur autre séjour négociable"
    }
  ];

  const circumstances = [
    {
      icon: "🏥",
      title: "Motifs Médicaux",
      conditions: [
        "Maladie grave du voyageur ou d'un proche",
        "Accident nécessitant une hospitalisation",
        "Prescription médicale d'arrêt de travail",
        "Complication de grossesse"
      ]
    },
    {
      icon: "⚖️", 
      title: "Motifs Légaux",
      conditions: [
        "Convocation devant un tribunal",
        "Licenciement économique imprévu",
        "Mutation professionnelle obligatoire",
        "Refus de visa"
      ]
    },
    {
      icon: "🌪️",
      title: "Force Majeure",
      conditions: [
        "Catastrophe naturelle sur le lieu de séjour",
        "Grève des transports (>24h)",
        "Fermeture de frontières",
        "État d'urgence déclaré"
      ]
    }
  ];

  const modificationOptions = [
    {
      title: "Report de Dates",
      description: "Changement gratuit des dates dans la même saison",
      fee: "Gratuit",
      conditions: "Sous réserve de disponibilité, même villa"
    },
    {
      title: "Changement de Villa",
      description: "Modification vers une autre propriété de valeur équivalente",
      fee: "50€ de frais administratifs", 
      conditions: "Ajustement tarifaire si nécessaire"
    },
    {
      title: "Transfert de Réservation",
      description: "Cession de votre réservation à un tiers",
      fee: "100€ de frais de transfert",
      conditions: "Validation du nouveau client requise"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <Badge 
            variant="outline" 
            className="mb-6 px-6 py-3 bg-white/50 backdrop-blur-sm border-gray-200 text-gray-700 text-sm font-light uppercase tracking-wider"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Flexibilité Totale
          </Badge>
          
          <h1 
            className="text-5xl md:text-6xl font-light mb-6 text-gray-800"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Politique d'Annulation
          </h1>
          
          <p 
            className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-8"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Des conditions flexibles adaptées aux imprévus de la vie, avec des options de report et modification généreuses
          </p>
        </div>
      </section>

      {/* Cancellation Timeline */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl font-light mb-6 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Barème d'Annulation
            </h2>
            <p className="text-lg text-gray-600">
              Plus vous annulez tôt, plus les conditions sont avantageuses
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {cancellationTiers.map((tier, index) => (
              <Card key={index} className={`p-8 text-center hover:shadow-xl transition-all duration-300 ${
                tier.color === 'green' ? 'border-green-200 bg-green-50/50' :
                tier.color === 'amber' ? 'border-amber-200 bg-amber-50/50' :
                'border-red-200 bg-red-50/50'
              }`}>
                <div className="mb-6">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                    tier.color === 'green' ? 'bg-green-100' :
                    tier.color === 'amber' ? 'bg-amber-100' :
                    'bg-red-100'
                  }`}>
                    <Calendar className={`w-8 h-8 ${
                      tier.color === 'green' ? 'text-green-600' :
                      tier.color === 'amber' ? 'text-amber-600' :
                      'text-red-600'
                    }`} />
                  </div>
                  
                  <h3 
                    className="text-xl font-light text-gray-800 mb-2"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {tier.period}
                  </h3>
                </div>
                
                <div className="mb-6">
                  <div className={`text-4xl font-light mb-2 ${
                    tier.color === 'green' ? 'text-green-600' :
                    tier.color === 'amber' ? 'text-amber-600' :
                    'text-red-600'
                  }`}>
                    {tier.refund}
                  </div>
                  <p className="text-sm text-gray-500 mb-4">de remboursement</p>
                  <Badge variant="outline" className={`${
                    tier.color === 'green' ? 'border-green-200 text-green-700' :
                    tier.color === 'amber' ? 'border-amber-200 text-amber-700' :
                    'border-red-200 text-red-700'
                  }`}>
                    {tier.fee}
                  </Badge>
                </div>
                
                <p className="text-sm text-gray-600 leading-relaxed">
                  {tier.description}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Special Circumstances */}
      <section className="py-16 px-6 bg-white/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl font-light mb-6 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Circonstances Exceptionnelles
            </h2>
            <p className="text-lg text-gray-600 mb-4">
              Annulation gratuite possible dans certaines situations imprévisibles
            </p>
            <Badge variant="outline" className="bg-blue-50 border-blue-200 text-blue-700">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Justificatifs requis
            </Badge>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {circumstances.map((circumstance, index) => (
              <Card key={index} className="p-6 hover:shadow-lg transition-all duration-300">
                <div className="text-center mb-6">
                  <div className="text-4xl mb-4">{circumstance.icon}</div>
                  <h3 
                    className="text-xl font-light text-gray-800"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {circumstance.title}
                  </h3>
                </div>
                
                <div className="space-y-3">
                  {circumstance.conditions.map((condition, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600">{condition}</span>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 max-w-2xl mx-auto">
              <h3 className="text-lg font-medium text-gray-800 mb-4">
                ⚡ Assurance Annulation Recommandée
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Pour une protection optimale, souscrivez à notre assurance annulation 
                qui couvre 100% des frais dans tous les cas de figure.
              </p>
              <Button size="sm" variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-100">
                En savoir plus sur l'assurance
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Card>
          </div>
        </div>
      </section>

      {/* Modification Options */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl font-light mb-6 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Alternatives à l'Annulation
            </h2>
            <p className="text-lg text-gray-600">
              Des solutions flexibles pour adapter votre séjour plutôt que l'annuler
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {modificationOptions.map((option, index) => (
              <Card key={index} className="p-6 hover:shadow-lg transition-all duration-300">
                <h3 
                  className="text-xl font-light text-gray-800 mb-4"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {option.title}
                </h3>
                
                <p className="text-gray-600 mb-4 leading-relaxed">
                  {option.description}
                </p>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Frais:</span>
                    <Badge variant="outline" className="text-green-700 border-green-200">
                      {option.fee}
                    </Badge>
                  </div>
                  
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Conditions:</p>
                    <p className="text-xs text-gray-600">{option.conditions}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process & Contact */}
      <section className="py-16 px-6 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl font-light mb-6 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Comment Procéder ?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-medium">1</span>
              </div>
              <h3 className="font-medium text-gray-800 mb-2">Contactez-nous</h3>
              <p className="text-sm text-gray-600">
                Téléphone, email ou via votre espace client
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-medium">2</span>
              </div>
              <h3 className="font-medium text-gray-800 mb-2">Justificatifs</h3>
              <p className="text-sm text-gray-600">
                Envoyez les documents requis si applicable
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-medium">3</span>
              </div>
              <h3 className="font-medium text-gray-800 mb-2">Traitement</h3>
              <p className="text-sm text-gray-600">
                Réponse sous 48h et remboursement sous 7 jours
              </p>
            </div>
          </div>

          <div className="text-center">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white px-8 py-3 rounded-full font-medium text-lg transition-all duration-300 transform hover:scale-105"
            >
              Gérer ma Réservation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            
            <p className="text-sm text-gray-500 mt-4">
              <Clock className="w-4 h-4 inline mr-1" />
              Support disponible 7j/7 de 7h30 à 19h30 EST
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CancellationPage;